import { defineCommand } from 'citty';
import * as p from '@clack/prompts';
import { mkdirSync, writeFileSync } from 'node:fs';
import { homedir } from 'node:os';
import { dirname } from 'node:path';
import { getGlobalConfigTomlPath, getProjectConfigTomlPath } from '../../config/config-paths.js';
import { getResolvedConfig, type ResolvedMemorixConfig } from '../../config/resolved-config.js';
import { detectProject } from '../../project/detector.js';

export default defineCommand({
  meta: {
    name: 'migrate',
    description: 'Write a TOML config from existing compatibility config',
  },
  args: {
    global: {
      type: 'boolean',
      description: 'Write ~/.memorix/config.toml instead of project memorix.toml',
      required: false,
    },
  },
  run: async ({ args }) => {
    const isGlobal = Boolean(args.global);
    const project = isGlobal ? null : detectProject(process.cwd());
    if (!isGlobal && !project) {
      p.log.error('Not inside a git project. Use `memorix config migrate --global` for global config.');
      return;
    }

    const target = isGlobal ? getGlobalConfigTomlPath(homedir()) : getProjectConfigTomlPath(project!.rootPath);
    const resolved = getResolvedConfig({ projectRoot: project?.rootPath ?? null });
    const content = serializeResolvedConfigToToml(resolved, { includeSecrets: isGlobal });
    mkdirSync(dirname(target), { recursive: true });
    writeFileSync(target, content, 'utf8');
    p.log.info(`Wrote ${target}`);
  },
});

export function serializeResolvedConfigToToml(
  config: ResolvedMemorixConfig,
  options: { includeSecrets?: boolean } = {},
): string {
  const includeSecrets = options.includeSecrets === true;
  const lines: string[] = [
    '# Memorix configuration',
    '# Generated by memorix config migrate',
    '',
  ];

  writeSection(lines, 'agent', {
    provider: config.agent.provider,
    model: config.agent.model,
    base_url: config.agent.baseUrl,
    api_key: includeSecrets ? config.agent.apiKey : undefined,
  });

  writeSection(lines, 'memory.llm', {
    provider: config.memory.llm.provider,
    model: config.memory.llm.model,
    base_url: config.memory.llm.baseUrl,
    api_key: includeSecrets ? config.memory.llm.apiKey : undefined,
  });

  writeSection(lines, 'embedding', {
    provider: config.embedding.provider,
    model: config.embedding.model,
    base_url: config.embedding.baseUrl,
    api_key: includeSecrets ? config.embedding.apiKey : undefined,
    dimensions: config.embedding.dimensions,
  });

  writeSection(lines, 'memory', {
    inject: config.memory.inject,
    formation: config.memory.formation,
    auto_cleanup: config.memory.autoCleanup,
  });

  writeSection(lines, 'git', {
    auto_hook: config.git.autoHook,
    ingest_on_commit: config.git.ingestOnCommit,
    max_diff_size: config.git.maxDiffSize,
    skip_merge_commits: config.git.skipMergeCommits,
    exclude_patterns: config.git.excludePatterns,
    noise_keywords: config.git.noiseKeywords,
  });

  writeSection(lines, 'server', {
    transport: config.server.transport,
    dashboard: config.server.dashboard,
    dashboard_port: config.server.dashboardPort,
    port: config.server.port,
  });

  return `${lines.join('\n').replace(/\n{3,}/g, '\n\n').trimEnd()}\n`;
}

function writeSection(lines: string[], name: string, values: Record<string, string | number | boolean | string[] | undefined>): void {
  const entries = Object.entries(values).filter(([, value]) => value !== undefined);
  if (entries.length === 0) return;
  lines.push(`[${name}]`);
  for (const [key, value] of entries) {
    lines.push(`${key} = ${formatTomlValue(value!)}`);
  }
  lines.push('');
}

function formatTomlValue(value: string | number | boolean | string[]): string {
  if (Array.isArray(value)) return `[${value.map((entry) => formatTomlValue(entry)).join(', ')}]`;
  if (typeof value === 'string') return `"${value.replace(/\\/g, '\\\\').replace(/"/g, '\\"')}"`;
  return String(value);
}
